import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CalendarClock, Check, ChevronDown, Clapperboard, Code2, Download, ExternalLink, Eye,
  FileText, Gauge, HelpCircle, Hourglass, Image as ImageIcon, Link2, ListChecks, Lock, Maximize2, Menu, MonitorPlay,
  PanelRight, Share2, Star, Youtube, X,
} from 'lucide-react';
import { toast } from 'sonner';
import Brand from '../../components/Brand';
import ModeToggle from '../../components/ModeToggle';
import { learnAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { t } from '../../lib/i18n';
import { pointsAPI, pointsFor, useLmsActions } from '../../lib/points';
import { btnGhost, btnPrimary, panelCls } from '../../components/ui';
import HtmlFrame from '../../components/HtmlFrame';
import LessonCheckModal from '../../components/LessonCheckModal';

const TYPE_ICON = {
  video: Clapperboard, youtube: Youtube, vimeo: MonitorPlay,
  url: Link2, iframe: MonitorPlay, document: FileText, image: ImageIcon, quiz: HelpCircle,
  wealth_scorecard: Gauge, html: Code2,
};

// Media lessons render on the dark "stage" above the tabs (Udemy register);
// quiz / scorecard / iframe stay in the Overview column.
const STAGE_TYPES = ['video', 'youtube', 'vimeo', 'document', 'image', 'url'];
const TYPE_LABEL = {
  video: 'Video', youtube: 'Video', vimeo: 'Video', url: 'Link', iframe: 'Embed',
  document: 'Document', image: 'Image', quiz: 'Quiz', wealth_scorecard: 'Scorecard', html: 'HTML page',
};

// Lesson text arrives as plain text. When the author uses run-in CAPS labels
// ("THE PUNCH:", "HOMEWORK (proof):") split them into paragraphs with a bold
// title-cased label; whatever precedes the first label becomes a small meta
// line. No labels → plain paragraphs (blank-line separated). Text only, never
// HTML, so nothing is injected.
const LABEL_RE = /\b((?:THE\s+)?[A-Z]{3,}(?:\s*\([^)]*\))?):\s*/g;
function formatSummary(raw) {
  const text = (raw || '').trim();
  if (!text) return null;
  const matches = [...text.matchAll(LABEL_RE)];
  if (matches.length < 2) {
    return { meta: '', blocks: text.split(/\n{2,}/).map((p) => ({ label: '', text: p.trim() })).filter((b) => b.text) };
  }
  const blocks = matches.map((m, i) => {
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    const label = m[1].replace(/\s*\([^)]*\)/, '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    return { label, text: text.slice(m.index + m[0].length, end).trim() };
  }).filter((b) => b.text);
  return { meta: text.slice(0, matches[0].index).trim(), blocks };
}

function ytId(url) {
  const m = (url || '').match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url) {
  const m = (url || '').match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

// ---------------------------------------------------------------- quiz view

function QuizView({ lesson, lastResult, onSubmitted }) {
  const questions = lesson.quiz?.questions || [];
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAnswers({});
    setResult(null);
  }, [lesson.id]);

  const toggle = (qid, i) => {
    setAnswers((prev) => {
      const cur = prev[qid] || [];
      return { ...prev, [qid]: cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i].sort() };
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await learnAPI.quizSubmit(lesson.id, answers);
      setResult(data);
      onSubmitted(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const byQ = Object.fromEntries(result.results.map((r) => [r.question_id, r]));
    return (
      <div className="space-y-5">
        <div className={`${panelCls} p-5 text-center`}>
          <div className="text-3xl font-semibold lms-num">{result.score} / {result.total}</div>
          <p className="mt-1 text-sm text-lms-muted">{t('Your score')}</p>
          <button onClick={() => { setAnswers({}); setResult(null); }} className={`${btnGhost} mt-4`}>
            {t('Retake quiz')}
          </button>
        </div>
        {questions.map((q, qi) => {
          const r = byQ[q.id] || {};
          return (
            <div key={q.id} className={`${panelCls} p-4`}>
              <p className="mb-2 text-sm font-medium">
                <span className="mr-2 text-lms-faint lms-num">Q{qi + 1}</span>{q.title}
                <span className={`ml-2 text-xs ${r.correct ? 'text-lms-success' : 'text-[var(--lms-danger)]'}`}>
                  {r.correct ? t('Correct') : t('Incorrect')}
                </span>
              </p>
              <ul className="space-y-1">
                {q.options.map((opt, i) => {
                  const picked = (r.picked || []).includes(i);
                  const isAnswer = (r.answer || []).includes(i);
                  return (
                    <li
                      key={i}
                      className={`rounded px-2.5 py-1.5 text-sm ${
                        isAnswer ? 'bg-lms-success/10 text-lms-success'
                          : picked ? 'bg-[var(--lms-danger)]/10 text-[var(--lms-danger)]'
                            : 'text-lms-muted'
                      }`}
                    >
                      {picked ? '● ' : '○ '}{opt}{isAnswer ? ` — ${t('answer')}` : ''}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {lastResult && (
        <p className="text-xs text-lms-faint">
          {t('Last attempt')}: <span className="lms-num">{lastResult.score}/{lastResult.total}</span>
        </p>
      )}
      {questions.map((q, qi) => (
        <div key={q.id} className={`${panelCls} p-4`}>
          <p className="mb-2 text-sm font-medium">
            <span className="mr-2 text-lms-faint lms-num">Q{qi + 1}</span>{q.title}
          </p>
          <ul className="space-y-1">
            {q.options.map((opt, i) => (
              <li key={i}>
                <label className="flex cursor-pointer items-center gap-2.5 rounded px-2.5 py-1.5 text-sm text-lms-muted hover:bg-lms-raised/60 hover:text-lms-text">
                  <input
                    type="checkbox"
                    checked={(answers[q.id] || []).includes(i)}
                    onChange={() => toggle(q.id, i)}
                  />
                  {opt}
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <button onClick={submit} disabled={busy} className={btnPrimary}>
        {busy ? t('Submitting…') : t('Submit answers')}
      </button>
    </div>
  );
}

// ------------------------------------------------------- wealth scorecard view

function ScorecardView({ lesson, slug, isDone, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  // On mount, reconcile the CMS submission → progress. This covers the member
  // who already filled the (once-per-member) baseline in another course, and the
  // in-SPA case where they reach this lesson without a full page reload.
  useEffect(() => {
    let alive = true;
    learnAPI.scorecardRefresh(lesson.id)
      .then(({ data }) => { if (alive) onRefreshed(data); })
      .catch(() => {})
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [lesson.id]);

  const launch = async () => {
    setBusy(true);
    try {
      const ret = `${window.location.origin}/learn/${slug}`;
      const { data } = await learnAPI.scorecardLaunch(lesson.id, ret);
      window.location.assign(data.url);   // same-tab hand-off to the CMS form
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Could not open the scorecard'));
      setBusy(false);
    }
  };

  if (isDone) {
    return (
      <div className={`${panelCls} p-8 text-center`}>
        <Gauge size={28} className="mx-auto mb-3 text-lms-success" />
        <p className="mb-1 font-medium">{t('Scorecard completed')}</p>
        <p className="mb-4 text-sm text-lms-muted">{t('You have completed your Wealth Baseline Scorecard. Continue to the next lesson.')}</p>
        <button onClick={launch} disabled={busy} className={btnGhost}>
          <ExternalLink size={15} />
          {busy ? t('Opening…') : t('Open it again')}
        </button>
      </div>
    );
  }
  return (
    <div className={`${panelCls} p-8 text-center`}>
      <Gauge size={28} className="mx-auto mb-3 text-lms-accent" />
      <p className="mb-1 font-medium">{t('Wealth Baseline Scorecard')}</p>
      <p className="mb-4 text-sm text-lms-muted">
        {t('Open the form and answer honestly — your name, email and location come from your profile. When you submit, you will return here and this lesson will be marked complete.')}
      </p>
      <button onClick={launch} disabled={busy || checking} className={btnPrimary}>
        <ExternalLink size={15} />
        {busy ? t('Opening…') : checking ? t('Checking…') : t('Open the Wealth Baseline Scorecard')}
      </button>
    </div>
  );
}

// ------------------------------------------------------------ html lesson view

function HtmlLessonView({ lesson }) {
  const [page, setPage] = useState(null);
  const [failed, setFailed] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    let alive = true;
    setPage(null);
    setFailed(false);
    learnAPI.lessonHtml(lesson.id)
      .then(({ data }) => { if (alive) setPage(data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [lesson.id]);
  const fullScreen = () => {
    const el = wrapRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };
  if (failed) return <p className="text-sm text-lms-sub">{t('Could not load this lesson.')}</p>;
  return (
    <div className="overflow-hidden rounded-[14px] border border-lms-line">
      <div className="flex items-center justify-between border-b border-lms-line bg-lms-surface py-1 pl-4 pr-2">
        <span className="flex items-center gap-2 text-[12.5px] font-semibold text-lms-sub">
          <Code2 size={14} /> {t('Interactive lesson')}
        </span>
        <button type="button" onClick={fullScreen} className="inline-flex h-10 items-center gap-1.5 px-3 text-[13px] font-bold text-lms-link hover:underline">
          <Maximize2 size={14} /> {t('Full screen')}
        </button>
      </div>
      {!page ? (
        <div className="flex h-72 items-center justify-center bg-lms-raised text-sm text-lms-sub">{t('Loading…')}</div>
      ) : (
        <HtmlFrame
          ref={wrapRef}
          key={lesson.id}
          html={page.content}
          title={lesson.title}
          height={page.height}
          fixedPx={page.fixed_px}
          className="bg-white [&:fullscreen]:overflow-auto [&:fullscreen_iframe]:!h-full"
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------ lesson renderer

function LessonRenderer({ lesson, lastQuiz, onQuizSubmitted, slug, isDone, onScorecardRefreshed }) {
  const type = lesson.lesson_type;
  const url = lesson.video?.url || '';
  const path = lesson.attachment?.path || '';

  // s137: the admin cleared this lesson's material (or hasn't added it yet).
  const empty = (['video', 'youtube', 'vimeo', 'url', 'iframe'].includes(type) && !url)
    || (['document', 'image'].includes(type) && !path)
    || (type === 'html' && !lesson.html)
    || (type === 'quiz' && !(lesson.quiz && lesson.quiz.questions && lesson.quiz.questions.length));
  if (empty) {
    return (
      <div className={`${panelCls} p-8 text-center`}>
        <FileText size={28} className="mx-auto mb-3 text-lms-faint" />
        <p className="mb-1 font-medium">{t('No content yet')}</p>
        <p className="text-sm text-lms-muted">{t('The material for this lesson is being prepared. Check back soon.')}</p>
      </div>
    );
  }

  if (type === 'quiz') return <QuizView lesson={lesson} lastResult={lastQuiz} onSubmitted={onQuizSubmitted} />;
  if (type === 'html') return <HtmlLessonView lesson={lesson} />;
  if (type === 'wealth_scorecard') return <ScorecardView lesson={lesson} slug={slug} isDone={isDone} onRefreshed={onScorecardRefreshed} />;
  if (type === 'video') {
    return (
      <video key={lesson.id} controls controlsList="nodownload" className="aspect-video w-full rounded-lg bg-black" src={url} />
    );
  }
  if (type === 'youtube') {
    const id = ytId(url);
    return id ? (
      <iframe
        key={lesson.id}
        title={lesson.title}
        src={`https://www.youtube-nocookie.com/embed/${id}`}
        className="aspect-video w-full rounded-lg border-0 bg-black"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    ) : <p className="text-sm text-lms-muted">{t('Invalid video URL.')}</p>;
  }
  if (type === 'vimeo') {
    const id = vimeoId(url);
    return id ? (
      <iframe
        key={lesson.id}
        title={lesson.title}
        src={`https://player.vimeo.com/video/${id}`}
        className="aspect-video w-full rounded-lg border-0 bg-black"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    ) : <p className="text-sm text-lms-muted">{t('Invalid video URL.')}</p>;
  }
  if (type === 'iframe') {
    return <iframe key={lesson.id} title={lesson.title} src={url} className="h-[70vh] w-full rounded-lg border border-lms-border bg-white" />;
  }
  if (type === 'url') {
    return (
      <div className={`${panelCls} p-8 text-center`}>
        <Link2 size={28} className="mx-auto mb-3 text-lms-accent" />
        <p className="mb-4 break-all text-sm text-lms-muted">{url}</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className={btnPrimary}>
          <ExternalLink size={15} />
          {t('Open resource')}
        </a>
      </div>
    );
  }
  if (type === 'document') {
    return (
      <div className={`${panelCls} p-8 text-center`}>
        <FileText size={28} className="mx-auto mb-3 text-lms-accent" />
        <p className="mb-4 text-sm text-lms-muted">{path.split('/').pop()}</p>
        <a href={path} target="_blank" rel="noopener noreferrer" className={btnPrimary}>
          <Download size={15} />
          {t('Open document')}
        </a>
      </div>
    );
  }
  if (type === 'image') {
    return <img key={lesson.id} src={path} alt={lesson.title} className="max-h-[70vh] w-full rounded-lg object-contain" />;
  }
  return null;
}

// ---------------------------------------------------------------- the player

export default function PlayerPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const wantLesson = search.get('lesson'); // s137: "Preview" on a curriculum row
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [pct, setPct] = useState(0);

  // "+N points" toasts (MMS_POINTS_HUB_PLAN F4): fire when the member's completed
  // count or progress goes UP after the course has loaded — never for the initial
  // load. Values come from the MMS rules; the MMS awards them a moment later.
  const lmsActions = useLmsActions();
  // staff previews never earn (backend _is_staff), so they get no "+N" toast either
  const earns = !['admin', 'manager'].includes((getUser() || {}).lr);
  const prevDone = useRef(null);
  const prevPct = useRef(null);
  useEffect(() => {
    if (!data || !earns) { prevDone.current = null; prevPct.current = null; return; }
    const n = completed.length;
    if (prevDone.current !== null && n > prevDone.current) {
      const pts = pointsFor(lmsActions, 'lesson_completed');
      if (pts) toast.success(`+${pts} ${t('points')}`, { description: t('Lesson completed') });
    }
    if (prevPct.current !== null && prevPct.current < 100 && pct >= 100) {
      const pts = pointsFor(lmsActions, 'course_completed');
      if (pts) toast.success(`+${pts} ${t('points')}`, { description: t('Course completed') });
    }
    prevDone.current = n;
    prevPct.current = pct;
  }, [data, completed, pct, lmsActions, earns]);
  const [openSections, setOpenSections] = useState({});
  const [sideOpen, setSideOpen] = useState(false);
  const [showSide, setShowSide] = useState(true);
  const [tab, setTab] = useState('overview');
  const [passedIds, setPassedIds] = useState([]);
  const [checkOpen, setCheckOpen] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    learnAPI.view(slug)
      .then(({ data: d }) => {
        setData(d);
        setCompleted(d.progress.completed_lesson_ids);
        setPct(d.progress.pct);
        setPassedIds(d.progress.check_passed_ids || []);
        const all = d.sections.flatMap((s) => s.lessons);
        // Sequential gate: never open a lesson past the first incomplete one.
        const doneIds = d.progress.completed_lesson_ids || [];
        const firstInc = all.findIndex((l) => !doneIds.includes(l.id));
        const maxIdx = firstInc === -1 ? all.length - 1 : firstInc;
        const savedIdx = d.progress.current_lesson_id ? all.findIndex((l) => l.id === d.progress.current_lesson_id) : -1;
        // A preview (staff / author) may jump straight to the lesson asked for.
        const wantIdx = d.preview && wantLesson ? all.findIndex((l) => l.id === wantLesson) : -1;
        const startIdx = wantIdx >= 0 ? wantIdx : savedIdx >= 0 ? Math.min(savedIdx, maxIdx) : 0;
        setCurrentId(all[Math.max(0, startIdx)]?.id || all[0]?.id || null);
        setOpenSections(Object.fromEntries(d.sections.map((s) => [s.id, true])));
      })
      .catch((err) => setDenied(err.response?.status || 500));
  }, [slug]);

  const allLessons = useMemo(() => (data ? data.sections.flatMap((s) => s.lessons) : []), [data]);
  const current = allLessons.find((l) => l.id === currentId) || null;
  const idx = allLessons.findIndex((l) => l.id === currentId);

  // Sequential gate: a lesson is unlocked only once every earlier lesson is
  // completed, so students go through the course step by step.
  const firstIncompleteIdx = useMemo(() => {
    const i = allLessons.findIndex((l) => !completed.includes(l.id));
    return i === -1 ? allLessons.length : i;
  }, [allLessons, completed]);
  const isLockedId = useCallback(
    (lid) => allLessons.findIndex((l) => l.id === lid) > firstIncompleteIdx,
    [allLessons, firstIncompleteIdx],
  );

  const selectLesson = useCallback((lid) => {
    if (isLockedId(lid)) { toast.error(t('Complete the previous lessons first.')); return; }
    setCurrentId(lid);
    setSideOpen(false);
    setCheckOpen(false);
    if (mainRef.current) mainRef.current.scrollTo({ top: 0 });
    if (data) learnAPI.progress(data.course.id, { current_lesson_id: lid }).catch(() => {});
  }, [data, isLockedId]);

  const markComplete = async (done = true) => {
    if (!current || !data) return;
    try {
      const { data: p } = await learnAPI.progress(data.course.id, { lesson_id: current.id, done });
      setCompleted(p.completed_lesson_ids);
      setPct(p.pct);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const lastQuiz = useMemo(() => {
    if (!data || !current) return null;
    const attempts = (data.progress.quiz_results || []).filter((r) => r.lesson_id === current.id);
    return attempts[attempts.length - 1] || null;
  }, [data, current]);

  if (denied === 403) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-center">
        <div>
          <p className="text-sm text-lms-muted">{t('You are not enrolled in this course.')}</p>
          <Link to={`/course/${slug}`} className={`${btnPrimary} mt-4`}>{t('View the course page')}</Link>
        </div>
      </div>
    );
  }
  if (denied) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-lms-muted">{t('Course not found')}</div>;
  }
  if (!data) return <div className="flex min-h-screen items-center justify-center text-sm text-lms-muted">{t('Loading…')}</div>;

  const isDone = current && completed.includes(current.id);
  const resetPreview = async () => {
    if (!window.confirm(t('Reset your preview progress? Every lesson goes back to not completed.'))) return;
    try {
      await learnAPI.resetPreview(data.course.id);
      setCompleted([]);
      setPct(0);
      setPassedIds([]);
      if (allLessons[0]) setCurrentId(allLessons[0].id);
      toast.success(t('Preview progress reset'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };
  const previewBar = data.preview ? (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-lms-tag-alt-bg px-4 py-2 text-[13px] font-semibold text-lms-tag-alt-ink">
      <span className="inline-flex items-center gap-1.5"><Eye size={14} /> {t('Preview mode')}
        {data.course.status && data.course.status !== 'active' ? ` · ${t(data.course.status === 'draft' ? 'Draft' : 'Pending review')}` : ''}
        {' — '}{t('you see the course as a student; your progress here is only a test.')}
      </span>
      <button type="button" onClick={resetPreview} className="underline underline-offset-2 hover:no-underline">{t('Reset my preview progress')}</button>
    </div>
  ) : null;
  const sectionOf = (lid) => data.sections.find((s) => s.lessons.some((l) => l.id === lid));
  const curSection = current ? sectionOf(current.id) : null;
  const onStage = current && STAGE_TYPES.includes(current.lesson_type);
  const isHtmlLesson = current && current.lesson_type === 'html';
  // Comprehension check: while it is not passed, "Mark complete" is replaced by
  // "Take the check" (the server enforces the same rule on PUT progress).
  const hasCheck = Boolean(current && current.check && current.check.questions && current.check.questions.length);
  const needsCheck = hasCheck && !isDone && !passedIds.includes(current.id);
  const selfGraded = current && (current.lesson_type === 'quiz' || current.lesson_type === 'wealth_scorecard');
  const nextLesson = idx >= 0 && idx < allLessons.length - 1 ? allLessons[idx + 1] : null;
  // s135 manual release: the payload only carries released lessons; when more
  // exist, the last one shown ends with "your mentor releases the next one".
  const moreComing = !!(data.release && data.release.manual && data.release.released < data.release.total);
  const onLastReleased = moreComing && idx === allLessons.length - 1;
  const lessonBody = current ? formatSummary(current.summary) : null;
  const goMentor = () => navigate(
    `/my/mentor?course=${encodeURIComponent(data.course.id || '')}&lesson=${encodeURIComponent(current?.id || '')}`,
    { state: { courseTitle: data.course.title, lessonTitle: current?.title } },
  );
  const share = async () => {
    const url = `${window.location.origin}/course/${slug}`;
    try {
      if (navigator.share) await navigator.share({ title: data.course.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t('Link copied'));
      }
      pointsAPI.share(data.course.id).catch(() => {});
    } catch {
      /* dismissed */
    }
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-lms-line pl-5 pr-2">
        <span className="text-base font-bold text-lms-ink">{t('Course content')}</span>
        <button
          type="button"
          onClick={() => { setSideOpen(false); setShowSide(false); }}
          aria-label={t('Close course content')}
          className="lms-press flex h-11 w-11 items-center justify-center text-lms-ink"
        >
          <X size={17} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {data.sections.map((s) => {
          const doneHere = s.lessons.filter((l) => completed.includes(l.id)).length;
          return (
            <div key={s.id} className="border-b border-lms-line">
              <button
                type="button"
                onClick={() => setOpenSections((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
                aria-expanded={!!openSections[s.id]}
                className="flex w-full items-start justify-between gap-3 bg-lms-raised px-5 py-3.5 text-left"
              >
                <span>
                  <span className="block text-[14.5px] font-bold leading-snug text-lms-ink">{s.title}</span>
                  <span className="mt-0.5 block text-xs text-lms-sub lms-num">{doneHere} / {s.lessons.length}</span>
                </span>
                <ChevronDown size={16} className={`mt-0.5 shrink-0 text-lms-ink transition-transform ${openSections[s.id] ? 'rotate-180' : ''}`} />
              </button>
              {openSections[s.id] && s.lessons.map((l) => {
                const n = allLessons.findIndex((x) => x.id === l.id) + 1;
                const active = l.id === currentId;
                const done = completed.includes(l.id);
                const locked = isLockedId(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => selectLesson(l.id)}
                    disabled={locked}
                    aria-current={active ? 'true' : undefined}
                    title={locked ? t('Complete the previous lessons first.') : undefined}
                    className={`flex w-full gap-3.5 px-5 py-3 text-left ${
                      active ? 'bg-lms-current' : locked ? 'cursor-not-allowed' : 'hover:bg-lms-raised'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded ${
                        done ? 'bg-lms-ink text-lms-surface' : `border-[1.5px] ${locked ? 'border-lms-faint' : 'border-lms-ink'}`
                      }`}
                    >
                      {done && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm leading-snug ${active ? 'font-semibold text-lms-ink' : locked ? 'text-lms-faint' : 'text-lms-body'}`}>
                        {/^(lesson|lecci[oó]n|clase|module|m[oó]dulo)\s*\d+/i.test(l.title || '') ? l.title : `${n}. ${l.title}`}
                      </span>
                      <span className={`mt-1 flex items-center gap-1.5 text-xs ${locked ? 'text-lms-faint' : 'text-lms-sub'}`}>
                        {locked ? <Lock size={11} /> : null}
                        {locked
                          ? (n - 2 === firstIncompleteIdx
                            ? (allLessons[n - 2]?.check
                              ? `${t('Pass the lesson')} ${n - 1} ${t('check to unlock')}`
                              : `${t('Complete lesson')} ${n - 1} ${t('to unlock')}`)
                            : t('Locked'))
                          : [
                            t(TYPE_LABEL[l.lesson_type] || 'Lesson'),
                            l.video?.duration,
                            l.check ? `${l.check.questions.length} ${l.check.questions.length === 1 ? t('question') : t('questions')}` : null,
                            active ? t('now playing') : null,
                          ].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className={`border-b-[3px] pb-3 pt-4 text-[15px] ${
        tab === key ? 'border-lms-ink font-bold text-lms-ink' : 'border-transparent font-semibold text-lms-sub hover:text-lms-ink'
      }`}
    >
      {label}
    </button>
  );

  const mentorBox = (
    <div className="flex gap-4 rounded-xl border border-lms-line p-5">
      <CalendarClock size={26} className="shrink-0 text-lms-ink" />
      <div className="flex flex-col gap-2">
        <span className="text-base font-bold text-lms-ink">{t('Questions about this lesson?')}</span>
        <span className="text-sm leading-relaxed text-lms-body">
          {t('Book a session with your mentor to go over it. Your booking will reference this lesson so your mentor comes prepared.')}
        </span>
        <div className="flex flex-wrap gap-2.5 pt-1">
          <button type="button" onClick={goMentor} className="lms-press inline-flex h-10 items-center rounded-lg border border-lms-link px-4 text-sm font-bold text-lms-link hover:bg-lms-raised" data-testid="player-book-mentor">
            {t('Book a session')}
          </button>
          <Link to="/my/mentor" className="inline-flex h-10 items-center px-3 text-sm font-bold text-lms-link hover:underline">
            {t('See my sessions')}
          </Link>
        </div>
      </div>
    </div>
  );

  const renderer = current && (
    <LessonRenderer
      lesson={current}
      lastQuiz={lastQuiz}
      onQuizSubmitted={(r) => { setCompleted((prev) => (prev.includes(current.id) ? prev : [...prev, current.id])); setPct(r.progress_pct); }}
      slug={slug}
      isDone={isDone}
      onScorecardRefreshed={(r) => { if (r.completed_lesson_ids) setCompleted(r.completed_lesson_ids); if (typeof r.pct === 'number') setPct(r.pct); }}
    />
  );

  return (
    <div className="flex h-screen flex-col">
      {checkOpen && current && hasCheck && (
        <LessonCheckModal
          lesson={current}
          lessonNumber={idx + 1}
          nextNumber={nextLesson ? idx + 2 : null}
          onClose={() => setCheckOpen(false)}
          onPassed={(r) => {
            setCompleted(r.completed_lesson_ids);
            setPassedIds(r.check_passed_ids || []);
            setPct(r.pct);
          }}
          onNext={() => { setCheckOpen(false); if (nextLesson) setCurrentId(nextLesson.id); if (mainRef.current) mainRef.current.scrollTo({ top: 0 }); if (nextLesson) learnAPI.progress(data.course.id, { current_lesson_id: nextLesson.id }).catch(() => {}); }}
          onReview={() => { setCheckOpen(false); if (mainRef.current) mainRef.current.scrollTo({ top: 0, behavior: 'smooth' }); }}
        />
      )}
      {previewBar}
      {/* ===== DARK TOP BAR ===== */}
      <header className="flex h-[60px] shrink-0 items-center gap-4 border-b border-white/10 bg-lms-band px-4 text-lms-band-text sm:px-5">
        <button type="button" onClick={() => setSideOpen(!sideOpen)} className="lg:hidden" aria-label={t('Course content')}>
          {sideOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
        <Brand size="sm" linkToHome />
        <span className="hidden h-6 w-px bg-white/25 sm:block" />
        <Link to={`/course/${slug}`} className="min-w-0 flex-1 truncate text-[15px] font-medium text-lms-band-text hover:underline">
          {data.course.title}
        </Link>
        <span className="hidden items-center gap-2.5 text-sm md:flex" title={`${pct}%`}>
          <span
            className="flex h-[34px] w-[34px] items-center justify-center rounded-full"
            style={{ background: `conic-gradient(var(--lms-accent) 0 ${pct}%, rgba(255,255,255,0.22) ${pct}% 100%)` }}
          >
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-lms-band text-[9.5px] font-bold lms-num">{pct}%</span>
          </span>
          {t('Your progress')}
        </span>
        <button type="button" onClick={share} className="lms-press hidden h-11 items-center gap-2 rounded-lg border border-lms-band-text px-4 text-sm font-bold sm:flex">
          {t('Share')} <Share2 size={15} />
        </button>
        {!showSide && (
          <button type="button" onClick={() => setShowSide(true)} className="lms-press hidden h-11 items-center gap-2 rounded-lg border border-lms-band-text px-4 text-sm font-bold lg:flex">
            <PanelRight size={15} /> {t('Course content')}
          </button>
        )}
        <ModeToggle onBand />
      </header>

      <div className="flex min-h-0 flex-1">
        <main ref={mainRef} className={`${sideOpen ? 'hidden lg:block' : 'block'} min-w-0 flex-1 overflow-y-auto bg-lms-surface`}>
          {!current ? (
            <p className="p-6 text-sm text-lms-muted">{t('This course has no lessons yet.')}</p>
          ) : (
            <>
              {/* ===== STAGE ===== */}
              {onStage && (
                <div className="flex justify-center bg-lms-stage px-4 py-8 sm:px-8">
                  <div className="w-full max-w-4xl">{renderer}</div>
                </div>
              )}

              {/* ===== HTML LESSON (full column width, above the tabs) ===== */}
              {isHtmlLesson && (
                <div className="flex flex-col gap-5 px-5 pt-7 sm:px-10">
                  <div className="flex flex-col gap-1.5">
                    {curSection && (
                      <span className="text-[13px] font-bold uppercase tracking-wide text-lms-link">{curSection.title}</span>
                    )}
                    <h1 className="text-[26px] font-bold leading-tight text-lms-ink sm:text-[28px]">{current.title}</h1>
                  </div>
                  {renderer}
                </div>
              )}

              {/* ===== TABS ===== */}
              <nav role="tablist" aria-label={t('Lesson')} className="flex gap-7 border-b border-lms-line px-5 sm:px-10">
                {tabBtn('overview', t('Overview'))}
                {tabBtn('mentor', t('Mentor sessions'))}
                {tabBtn('reviews', t('Reviews'))}
              </nav>

              <div className="flex max-w-[900px] flex-col gap-6 px-5 pb-12 pt-7 sm:px-10">
                {tab === 'overview' && (
                  <>
                    {!isHtmlLesson && <div className="flex flex-col gap-1.5">
                      {curSection && (
                        <span className="text-[13px] font-bold uppercase tracking-wide text-lms-link">{curSection.title}</span>
                      )}
                      <h1 className="text-[26px] font-bold leading-tight text-lms-ink sm:text-[28px]">{current.title}</h1>
                    </div>}

                    <div className="flex flex-wrap gap-x-10 gap-y-3 border-y border-lms-line py-4">
                      <div>
                        <div className="text-lg font-bold text-lms-ink lms-num">{idx + 1} {t('of')} {allLessons.length}</div>
                        <div className="text-[12.5px] text-lms-sub">{t('Lesson')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-lms-ink lms-num">{completed.length} / {allLessons.length}</div>
                        <div className="text-[12.5px] text-lms-sub">{t('Completed')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-lms-ink">{t(TYPE_LABEL[current.lesson_type] || 'Lesson')}</div>
                        <div className="text-[12.5px] text-lms-sub">{t('Type')}</div>
                      </div>
                    </div>

                    {!onStage && !isHtmlLesson && renderer}

                    {lessonBody && (
                      <div className="lms-prose">
                        {lessonBody.meta && <p className="text-[13px] font-semibold uppercase tracking-wide text-lms-sub">{lessonBody.meta}</p>}
                        {lessonBody.blocks.map((b, i) => (
                          <p key={i}>
                            {b.label && <strong>{b.label}. </strong>}
                            {b.text}
                          </p>
                        ))}
                      </div>
                    )}

                    {needsCheck && !selfGraded && (
                      <div className="flex flex-col gap-4 rounded-[14px] border border-lms-tag-free-bg bg-lms-tag-free-bg/40 p-5 sm:flex-row sm:items-center">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lms-link text-lms-accent-ink">
                          <ListChecks size={22} />
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-base font-bold text-lms-ink">{t('Check your understanding')}</span>
                          <span className="text-[13.5px] leading-relaxed text-lms-body">
                            {current.check.questions.length} {t('quick questions about this lesson.')}{' '}
                            {current.check.pass_pct
                              ? `${t('Get')} ${current.check.required} ${t('right to complete it')}`
                              : t('Answer them all to complete it')}
                            {nextLesson ? ` ${t('and unlock lesson')} ${idx + 2}.` : '.'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCheckOpen(true)}
                          className="lms-press h-[46px] shrink-0 rounded-[10px] bg-lms-link px-6 text-[14.5px] font-bold text-lms-accent-ink hover:brightness-110"
                        >
                          {t('Take the check')}
                        </button>
                      </div>
                    )}

                    {/* Sequential gate: Next only after "Mark complete". */}
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => idx > 0 && selectLesson(allLessons[idx - 1].id)}
                        disabled={idx <= 0}
                        className="lms-press inline-flex h-11 items-center gap-2 rounded-lg border border-lms-ink px-4 text-sm font-bold text-lms-ink hover:bg-lms-raised disabled:cursor-not-allowed disabled:border-lms-line disabled:text-lms-faint"
                      >
                        <ArrowLeft size={15} /> {t('Previous')}
                      </button>
                      {!selfGraded && !needsCheck && (
                        <button
                          type="button"
                          onClick={() => markComplete(!isDone)}
                          className={isDone
                            ? 'lms-press inline-flex h-11 items-center gap-2 rounded-lg border border-lms-line px-5 text-sm font-bold text-lms-sub hover:text-lms-ink'
                            : 'lms-press inline-flex h-11 items-center gap-2 rounded-lg bg-lms-accent px-5 text-sm font-bold text-lms-accent-ink hover:brightness-110'}
                        >
                          <Check size={15} /> {isDone ? t('Completed — undo') : t('Mark complete')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (idx >= allLessons.length - 1) return;
                          if (!isDone) { toast.error(t('Complete this lesson to continue.')); return; }
                          selectLesson(allLessons[idx + 1].id);
                        }}
                        disabled={idx >= allLessons.length - 1 || !isDone}
                        title={!isDone ? t('Complete this lesson to continue.') : undefined}
                        className="lms-press inline-flex h-11 items-center gap-2 rounded-lg border border-lms-ink px-4 text-sm font-bold text-lms-ink hover:bg-lms-raised disabled:cursor-not-allowed disabled:border-lms-line disabled:text-lms-faint"
                      >
                        {t('Next')} <ArrowRight size={15} />
                      </button>
                    </div>
                    {!isDone && !selfGraded && idx < allLessons.length - 1 && (
                      <p className="-mt-3 text-center text-xs text-lms-sub">
                        {needsCheck ? t('Pass the check to unlock the next lesson.') : t('Mark this lesson complete to unlock the next one.')}
                      </p>
                    )}
                    {onLastReleased && (
                      <div role="status" className="flex items-start gap-3 rounded-xl border border-lms-line bg-lms-raised px-5 py-4">
                        <Hourglass size={18} className="mt-0.5 shrink-0 text-lms-ink" />
                        <div className="text-sm">
                          <p className="font-bold text-lms-ink">
                            {isDone ? t('Well done — your next lesson is on its way') : t('This is your latest lesson')}
                          </p>
                          <p className="mt-0.5 text-lms-body">
                            {isDone
                              ? t('Your mentor releases each lesson personally. You will get an email as soon as the next one is available.')
                              : t('Complete it and your mentor will release the next lesson. You will get an email when it is available.')}
                          </p>
                        </div>
                      </div>
                    )}

                    {mentorBox}
                  </>
                )}

                {tab === 'mentor' && mentorBox}

                {tab === 'reviews' && (
                  <div className="flex flex-col items-start gap-3 rounded-xl border border-lms-line p-5">
                    <span className="text-base font-bold text-lms-ink">{t('How is this course going?')}</span>
                    <span className="text-sm text-lms-body">{t('Your rating helps other members choose — and helps the instructor improve.')}</span>
                    <div className="flex flex-wrap gap-2.5 pt-1">
                      <Link to={`/course/${slug}?rate=1`} className="lms-press inline-flex h-10 items-center gap-2 rounded-lg bg-lms-accent px-4 text-sm font-bold text-lms-accent-ink hover:brightness-110">
                        <Star size={15} /> {t('Rate this course')}
                      </Link>
                      <Link to={`/course/${slug}`} className="inline-flex h-10 items-center px-3 text-sm font-bold text-lms-link hover:underline">
                        {t('See all reviews')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </main>

        {/* ===== COURSE CONTENT (right) ===== */}
        <aside
          className={`${sideOpen ? 'block' : 'hidden'} ${showSide ? 'lg:block' : 'lg:hidden'} w-full shrink-0 overflow-hidden border-l border-lms-line bg-lms-surface lg:w-[400px]`}
        >
          {sidebar}
        </aside>
      </div>
    </div>
  );
}
